
import { useState } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { Button } from "@/components/ui/button";
import { Locate } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { ReactNode } from "react";

interface InterventionGoogleMapProps {
  isLoaded: boolean;
  mapCenter: google.maps.LatLngLiteral;
  mapZoom: number;
  children: ReactNode;
  toast: typeof toast;
}

// Map container styles
const containerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '0.5rem'
};

const InterventionGoogleMap = ({ 
  isLoaded, 
  mapCenter, 
  mapZoom, 
  children, 
  toast 
}: InterventionGoogleMapProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [userMarker, setUserMarker] = useState<google.maps.Marker | null>(null);

  // Géolocalisation de l'utilisateur
  const handleLocateMe = () => {
    if (!map) return;

    if (navigator.geolocation) {
      toast({
        title: "Localisation en cours...",
        description: "Nous recherchons votre position actuelle.",
        duration: 2000,
      });

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          // Supprimer le marqueur précédent s'il existe
          if (userMarker) {
            userMarker.setMap(null);
          }

          // Créer un nouveau marqueur
          const marker = new google.maps.Marker({
            position: pos,
            map,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "#4285F4",
              fillOpacity: 1,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
            },
            title: "Votre position",
            animation: google.maps.Animation.DROP,
          });

          setUserMarker(marker);

          // Centrer la carte sur la position
          map.setCenter(pos);
          map.setZoom(10);

          // Déterminer la région la plus proche
          let closestRegion = "";
          let minDistance = Number.MAX_VALUE;

          const regions = ["Occitanie", "Auvergne-Rhône-Alpes", "Île-de-France"];
          const regionCenters = {
            "Occitanie": { lat: 43.7, lng: 2.0 },
            "Auvergne-Rhône-Alpes": { lat: 45.5, lng: 3.5 },
            "Île-de-France": { lat: 48.8, lng: 2.3 }
          };

          regions.forEach(region => {
            const regionCenter = regionCenters[region as keyof typeof regionCenters];
            
            // Distance euclidienne approximative
            const distance = Math.sqrt(
              Math.pow(regionCenter.lat - pos.lat, 2) + Math.pow(regionCenter.lng - pos.lng, 2)
            );

            if (distance < minDistance) {
              minDistance = distance;
              closestRegion = region;
            }
          });

          if (closestRegion) {
            toast({
              title: "Position trouvée !",
              description: `Vous êtes proche de la région : ${closestRegion}`,
              duration: 3000,
            });
          } else {
            toast({
              title: "Position trouvée !",
              description: "La carte a été centrée sur votre position actuelle.",
              duration: 3000,
            });
          }
        },
        (error) => {
          console.error("Erreur de géolocalisation:", error);
          toast({
            title: "Erreur de localisation",
            description: "Impossible de déterminer votre position. Vérifiez vos paramètres de confidentialité.",
            variant: "destructive",
          });
        }
      );
    } else {
      toast({
        title: "Géolocalisation non supportée",
        description: "Votre navigateur ne prend pas en charge la géolocalisation.",
        variant: "destructive",
      });
    }
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <p>Chargement de la carte...</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={mapZoom}
        options={{
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          styles: [
            {
              featureType: "all",
              elementType: "labels.text.fill",
              stylers: [{ color: "#6c7b8b" }]
            },
            {
              featureType: "administrative",
              elementType: "geometry",
              stylers: [{ visibility: "on" }]
            },
            {
              featureType: "road",
              elementType: "geometry",
              stylers: [{ color: "#ffffff" }]
            },
            {
              featureType: "water",
              elementType: "geometry.fill",
              stylers: [{ color: "#e9e9e9" }]
            }
          ]
        }}
        onLoad={setMap}
      >
        {children}
      </GoogleMap>
      
      {/* Bouton de géolocalisation */}
      <Button 
        onClick={handleLocateMe} 
        className="absolute top-4 right-4 bg-white hover:bg-gray-100 text-fabre-gray shadow-md z-10"
        size="sm"
        variant="outline"
      >
        <Locate className="h-5 w-5 mr-2 text-fabre-green" />
        Me localiser
      </Button>
      
      {/* Gradient overlay for the map */}
      <div className="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
    </div>
  );
};

export default InterventionGoogleMap;
